import { HttpBackend, HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/auth';
import { environment } from '@lohi/environments/environment';
import { NGXLogger } from 'ngx-logger';
import { omit } from 'remeda';
import { BehaviorSubject, combineLatest, interval, Observable, of, Subject, throwError } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  pluck,
  shareReplay,
  switchMap,
  take,
  tap,
  throttleTime,
} from 'rxjs/operators';
import { LOAD_ID } from '../loads/loads-routing.constants';
import { SnackBarService } from '../shared/snackbar.service';
import {
  BillingOrdersResponse,
  BillingSummary,
  CompletedLoadsSortables,
  EditLoadRequest,
  LoHiAdminInProgressLoadListItem,
  LohiLoadAllowedChargeType,
  LoHiLoadEditableInfo,
  LohiLoadV2,
  lohiLoadV2ToAdminBillingListItem,
  SettlementStatementPaymentIssueType,
  TourBillingDetails,
  TourBrokerCharge,
  UploadImageCategory,
} from '../types';
import { isClientError, isHttpErrorResponse } from '../utilities/isHttpErrorResponse';
import { loadSorting, saveSorting } from '../utilities/sortingSave';
import { RouterStateService } from './router-state.service';

interface CompletedOrdersRequest {
  limit: number;
  offset: number;
  sort: CompletedLoadsSortables;
  order: 'ASC' | 'DESC';
  search?: string;
  missingPOD?: string;
  missingPOD24Hrs?: string;
  source?: string;
}

@Injectable({
  providedIn: 'root',
})
export class LoadsForAllProductsService {
  private completedLoadsRequestObj$$ = new BehaviorSubject<CompletedOrdersRequest>({
    limit: 100,
    offset: 0,
    sort: 'completed_timestamp',
    order: 'DESC',
  });

  public get searchTerm$(): Observable<string | null> {
    return this.completedLoadsRequestObj$$.asObservable().pipe(pluck('search'));
  }

  private currentLoad$$ = new BehaviorSubject<LohiLoadV2>(null);
  private tourLoadCharges$$ = new BehaviorSubject<TourBrokerCharge[] | null>(null);
  private tourLoadBillingDetails$$ = new BehaviorSubject<TourBillingDetails | null>(null);
  private inProgressLoads$$ = new BehaviorSubject<LoHiAdminInProgressLoadListItem[]>(null);
  private inProgressLoadsThrottle$$ = new Subject();
  private chargeTypes$$ = new BehaviorSubject<LohiLoadAllowedChargeType[]>(null);
  private inProgressLoadsShared$: Observable<
    LoHiAdminInProgressLoadListItem[]
  > = this.inProgressLoads$$.asObservable().pipe(
    distinctUntilChanged(),
    map((inProgressLoads) => (inProgressLoads ? inProgressLoads : [])),
    shareReplay(1),
  );
  private inProgressLoadsPhoneMapShared$: Observable<
    Record<string, LoHiAdminInProgressLoadListItem>
  > = this.inProgressLoadsShared$.pipe(
    map((loads) =>
      loads.reduce((acc, load) => {
        acc[load.phone] = load;
        return acc;
      }, {}),
    ),
    shareReplay(1),
  );

  private inProgressLoadsIdMapShared$: Observable<
    Record<number, LoHiAdminInProgressLoadListItem>
  > = this.inProgressLoadsShared$.pipe(
    map((loads) =>
      loads.reduce((acc, load) => {
        acc[load.id] = load;
        return acc;
      }, {}),
    ),
    shareReplay(1),
  );

  private billingSummary$$ = new BehaviorSubject<BillingSummary>(null);

  public currentLoad$ = this.currentLoad$$.asObservable().pipe(shareReplay(1));
  public tourLoadCharges$ = this.tourLoadCharges$$.asObservable().pipe(shareReplay(1));
  public tourBillingDetails$ = this.tourLoadBillingDetails$$.asObservable().pipe(shareReplay(1));
  public chargeTypes$ = this.chargeTypes$$.asObservable().pipe(shareReplay(1));

  public get inProgressLoads$(): Observable<LoHiAdminInProgressLoadListItem[]> {
    this.inProgressLoadsThrottle$$.next();
    return this.inProgressLoadsShared$;
  }

  public get inProgressLoadsPhoneMap$(): Observable<Record<string, LoHiAdminInProgressLoadListItem>> {
    this.inProgressLoadsThrottle$$.next();
    return this.inProgressLoadsPhoneMapShared$;
  }

  public get inProgressLoadsIdMap$(): Observable<Record<string, LoHiAdminInProgressLoadListItem>> {
    this.inProgressLoadsThrottle$$.next();
    return this.inProgressLoadsIdMapShared$;
  }

  public get billingSummary$(): Observable<BillingSummary> {
    return this.billingSummary$$.pipe(filter((summary) => !!summary));
  }

  private completedLoadsPaginated$$ = new BehaviorSubject<BillingOrdersResponse>(null);
  private completedLoadsPaginatedShared$ = this.completedLoadsPaginated$$.asObservable().pipe(shareReplay(1));

  public get completedLoadsPaginated$(): Observable<BillingOrdersResponse> {
    return this.completedLoadsPaginatedShared$;
  }

  private completedLoadsPaginatedLoading$$ = new BehaviorSubject<boolean>(false);
  public completedLoadsPaginatedLoading$ = this.completedLoadsPaginatedLoading$$.pipe(shareReplay(1));

  constructor(
    private httpClient: HttpClient,
    private httpBackend: HttpBackend,
    private logger: NGXLogger,
    private routerState: RouterStateService,
    private afAuth: AngularFireAuth,
    private snackbar: SnackBarService,
  ) {
    const sortingData = loadSorting('completed-loads-v2');
    if (sortingData) {
      this.completedLoadsRequestObj$$.next({
        ...this.completedLoadsRequestObj$$.value,
        sort: sortingData.sortBy,
        order: sortingData.sortDirection ? 'ASC' : 'DESC',
      });
    }
    this.inProgressLoadsThrottle$$.pipe(throttleTime(1000)).subscribe(() => {
      this.loadInProgressLoads();
    });

    this.listenForCurrentLoad();
    this.listenForCurrentLoHiLoad();

    interval(5 * 60 * 1000).subscribe(() => {
      if (environment.pollingEnabled) {
        this.inProgressLoadsThrottle$$.next();
      }
    });

    this.completedLoadsRequestObj$$
      .pipe(
        tap((value) => {
          saveSorting('completed-loads-v2', value.sort, value.order === 'ASC');
        }),
        debounceTime(100),
        switchMap((value) => {
          this.completedLoadsPaginatedLoading$$.next(true);
          return this.loadCompletedLoadsV2$(value);
        }),
      )
      .subscribe({
        next: (completedLoads) => {
          this.completedLoadsPaginated$$.next(completedLoads);
          this.completedLoadsPaginatedLoading$$.next(false);
        },
        error: (error) => {
          this.logger.error(error);
          this.completedLoadsPaginatedLoading$$.next(false);
        },
      });
    this.loadBillingSummary();
    this.loadChargeTypes();
  }

  public nextCompletedLoadsPage() {
    const value = this.completedLoadsRequestObj$$.value;
    if (!value) {
      return;
    }
    const pagination = this.completedLoadsPaginated$$.value?.page;
    if (!pagination) {
      return;
    }
    const newOffset = value.offset + value.limit;
    if (newOffset > pagination.total) {
      return;
    }
    this.completedLoadsRequestObj$$.next({
      ...value,
      offset: value.offset + value.limit,
    });
  }

  public previousCompletedLoadsPage() {
    const value = this.completedLoadsRequestObj$$.value;
    if (!value) {
      return;
    }
    const offset = Math.max(value.offset - value.limit, 0);
    if (offset === value.offset) {
      return;
    }
    this.completedLoadsRequestObj$$.next({
      ...value,
      offset,
    });
  }

  public setCompletedLoadsSort(sortHeader: CompletedLoadsSortables) {
    const value = this.completedLoadsRequestObj$$.value;
    if (!value) {
      return;
    }
    if (value.sort === sortHeader) {
      this.completedLoadsRequestObj$$.next({
        ...value,
        order: value.order === 'ASC' ? 'DESC' : 'ASC',
        offset: 0,
      });
    } else {
      this.completedLoadsRequestObj$$.next({
        ...value,
        sort: sortHeader,
        order: 'ASC',
        offset: 0,
      });
    }
  }

  public setCompletedLoadsSearch(searchTerm: string) {
    const value = this.completedLoadsRequestObj$$.value;
    if (!value || (!value.search && !searchTerm) || value.search === searchTerm) {
      return;
    }
    if (searchTerm) {
      this.completedLoadsRequestObj$$.next({
        ...value,
        search: searchTerm,
        offset: 0,
      });
    } else {
      this.completedLoadsRequestObj$$.next({
        ...omit(value, ['search']),
        offset: 0,
      });
    }
  }

  public setCompletedLoadsShowFilters(filterValue: string) {
    const value = this.completedLoadsRequestObj$$.value;
    if (!value || !filterValue) {
      return;
    }
    if (filterValue && filterValue !== 'all') {
      this.completedLoadsRequestObj$$.next({
        ...omit(value, ['missingPOD', 'missingPOD24Hrs']),
        [filterValue]: true,
        offset: 0,
      });
    } else {
      this.completedLoadsRequestObj$$.next({
        ...omit(value, ['missingPOD', 'missingPOD24Hrs']),
        offset: 0,
      });
    }
  }

  public async uploadFile(fileToUpload: File, category: UploadImageCategory, name?: string): Promise<boolean> {
    const loadId = this.currentLoad$$.value?.uniqueId;
    if (!loadId) {
      return false;
    }
    const idToken = await this.afAuth.idToken.pipe(take(1)).toPromise();
    try {
      const formData = new FormData();
      formData.append('file', fileToUpload, name || fileToUpload.name);
      // Use custom one because we need this thing to generate the headers for multi part
      const client = new HttpClient(this.httpBackend);
      await client
        .post<null>(`${environment.api}/v2/load/${loadId}/upload`, formData, {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
          params: {
            category,
          },
        })
        .toPromise();
      await this.refreshCurrentLoad();
      return true;
    } catch (error) {
      this.logger.error(error);
      return false;
    }
  }

  public getEditableLoadInfo$(): Observable<LoHiLoadEditableInfo> {
    const loadId = this.currentLoad$$.value?.uniqueId;
    if (!loadId) {
      return throwError('No Current Order');
    }
    return this.httpClient.get<LoHiLoadEditableInfo>(`${environment.api}/v2/load/${loadId}/editable_info`);
  }

  public async editLoad$(editReq: EditLoadRequest): Promise<boolean> {
    const loadId = this.currentLoad$$.value?.uniqueId;
    if (!loadId) {
      return false;
    }
    try {
      await this.httpClient.post<null>(`${environment.api}/v2/load/${loadId}/edit`, editReq).toPromise();
      await this.refreshCurrentLoad();
      return true;
    } catch (error) {
      if (isHttpErrorResponse(error) && isClientError(error.error)) {
        this.snackbar.showError(error.error.messageToUser);
      } else {
        this.snackbar.showError('failed to update load');
      }
      this.logger.error(error);
      return false;
    }
  }

  public async updateAuditedState(loadId: string, isAudited: boolean): Promise<boolean> {
    if (!isAudited) {
      return;
    }
    try {
      await this.httpClient
        .post<{ load: LohiLoadV2 }>(`${environment.api}/v2/load/${loadId}/audit/approve`, {})
        .toPromise();
      const completedLoads = await this.loadCompletedLoadsV2$(this.completedLoadsRequestObj$$.value).toPromise();
      this.completedLoadsPaginated$$.next(completedLoads);
      return true;
    } catch (error) {
      this.logger.error(error);
      // force a repaint
      const currentResponse = this.completedLoadsPaginated$$.value;
      if (!currentResponse) {
        return;
      }
      const currentCompletedOrders = currentResponse.loads || [];
      const index = currentCompletedOrders.findIndex((order) => order.uniqueId === `${loadId}`);
      if (index > 0) {
        const copy = [...currentCompletedOrders];
        copy[index] = {
          ...currentCompletedOrders[index],
        };
        this.completedLoadsPaginated$$.next({
          ...currentResponse,
          loads: copy,
        });
      }
      return false;
    }
  }

  public async updateInvoicedState(loadId: string, isInvoiced: boolean): Promise<boolean> {
    try {
      const results = await this.httpClient
        .post<{ load: LohiLoadV2 }>(`${environment.api}/v2/load/${loadId}/toggle_order_invoiced`, {
          isInvoiced,
        })
        .toPromise();
      const currentResponse = this.completedLoadsPaginated$$.value;
      if (!currentResponse) {
        return;
      }
      const currentCompletedOrders = currentResponse.loads || [];
      const index = currentCompletedOrders.findIndex((order) => order.uniqueId === `${results.load.uniqueId}`);
      if (index >= 0) {
        const copy = [...currentCompletedOrders];
        copy[index] = lohiLoadV2ToAdminBillingListItem(results.load);
        this.completedLoadsPaginated$$.next({
          ...currentResponse,
          loads: copy,
        });
      }
      return true;
    } catch (error) {
      this.logger.error(error);
      // force a repaint
      const currentResponse = this.completedLoadsPaginated$$.value;
      if (!currentResponse) {
        return;
      }
      const currentCompletedOrders = currentResponse.loads || [];
      const index = currentCompletedOrders.findIndex((order) => order.uniqueId === `${loadId}`);
      if (index > 0) {
        const copy = [...currentCompletedOrders];
        copy[index] = {
          ...currentCompletedOrders[index],
        };
        this.completedLoadsPaginated$$.next({
          ...currentResponse,
          loads: copy,
        });
      }
      return false;
    }
  }

  public async addTourCharge(chargeType: LohiLoadAllowedChargeType, cents: number): Promise<boolean> {
    const load = this.currentLoad$$.value;
    if (!load || !load.id.startsWith('LLL')) {
      return false;
    }
    try {
      const response = await this.httpClient
        .post<{ charges: TourBrokerCharge[] }>(`${environment.api}/v2/load/${load.id}/tour_billing/charge`, {
          id: chargeType.id,
          name: chargeType.displayName,
          cents,
        })
        .pipe(map((resp) => resp.charges || []))
        .toPromise();
      this.tourLoadCharges$$.next(response);
      return true;
    } catch (err) {
      this.logger.error(err);
      if (isHttpErrorResponse(err)) {
        if (isClientError(err)) {
          this.snackbar.showError(err.messageToUser);
        } else {
          this.snackbar.showError('Failed to add charge');
        }
      } else {
        this.snackbar.showError('Failed to add charge');
      }
    }
    return false;
  }

  public async editTourCharge(id: number, chargeTypeNameId: number, name: string, cents: number): Promise<boolean> {
    const load = this.currentLoad$$.value;
    if (!load || !load.id.startsWith('LLL')) {
      return false;
    }
    try {
      const response = await this.httpClient
        .put<{ charges: TourBrokerCharge[] }>(`${environment.api}/v2/load/${load.id}/tour_billing/charge/${id}`, {
          chargeTypeNameId,
          name,
          cents,
        })
        .pipe(map((resp) => resp.charges || []))
        .toPromise();
      this.tourLoadCharges$$.next(response);
      return true;
    } catch (err) {
      this.logger.error(err);
      if (isHttpErrorResponse(err)) {
        if (isClientError(err)) {
          this.snackbar.showError(err.messageToUser);
        } else {
          this.snackbar.showError('Failed to update charge');
        }
      } else {
        this.snackbar.showError('Failed to update charge');
      }
    }
    return false;
  }

  public async deleteTourCharge(id: number): Promise<boolean> {
    const load = this.currentLoad$$.value;
    if (!load || !load.id.startsWith('LLL')) {
      return false;
    }
    try {
      const response = await this.httpClient
        .delete<{ charges: TourBrokerCharge[] }>(`${environment.api}/v2/load/${load.id}/tour_billing/charge/${id}`)
        .pipe(map((resp) => resp.charges || []))
        .toPromise();
      this.tourLoadCharges$$.next(response);
      return true;
    } catch (err) {
      this.logger.error(err);
      if (isHttpErrorResponse(err)) {
        if (isClientError(err)) {
          this.snackbar.showError(err.messageToUser);
        } else {
          this.snackbar.showError('Failed to delete charge');
        }
      } else {
        this.snackbar.showError('Failed to delete charge');
      }
    }
    return false;
  }

  public async uploadTourChargeFile(charge: TourBrokerCharge, fileToUpload: File): Promise<boolean> {
    const idToken = await this.afAuth.idToken.pipe(take(1)).toPromise();
    try {
      const formData = new FormData();
      formData.append('file', fileToUpload, fileToUpload.name);
      // Use custom one because we need this thing to generate the headers for multi part
      const client = new HttpClient(this.httpBackend);
      const response = await client
        .post<{ charges: TourBrokerCharge[] }>(
          `${environment.api}/v2/load/${charge.loadID}/tour_billing/charge/${charge.id}/upload`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${idToken}`,
            },
          },
        )
        .toPromise();
      this.tourLoadCharges$$.next(response?.charges || []);
      return true;
    } catch (err) {
      this.logger.error(err);
      if (isHttpErrorResponse(err)) {
        if (isClientError(err)) {
          this.snackbar.showError(err.messageToUser);
        } else {
          this.snackbar.showError('Failed to upload file');
        }
      } else {
        this.snackbar.showError('Failed to upload file');
      }
    }
  }

  public async deleteTourChargeFile(chargeID: number, fileID: number): Promise<boolean> {
    const load = this.currentLoad$$.value;
    if (!load || !load.id.startsWith('LLL')) {
      return false;
    }
    try {
      const response = await this.httpClient
        .delete<{ charges: TourBrokerCharge[] }>(
          `${environment.api}/v2/load/${load.id}/tour_billing/charge/${chargeID}/upload/${fileID}`,
        )
        .pipe(map((resp) => resp.charges || []))
        .toPromise();
      this.tourLoadCharges$$.next(response);
      return true;
    } catch (err) {
      this.logger.error(err);
      if (isHttpErrorResponse(err)) {
        if (isClientError(err)) {
          this.snackbar.showError(err.messageToUser);
        } else {
          this.snackbar.showError('Failed to delete charge');
        }
      } else {
        this.snackbar.showError('Failed to delete charge');
      }
    }
    return false;
  }

  public async approveCurrentTourLoad(): Promise<boolean> {
    const load = this.currentLoad$$.value;
    if (!load || !load.id.startsWith('LLL')) {
      return false;
    }
    return this.approveTourLoad(load.id);
  }

  public async approveTourLoad(loadID: string): Promise<boolean> {
    try {
      const response = await this.httpClient
        .post<{ details: TourBillingDetails }>(`${environment.api}/v2/load/${loadID}/tour_billing/admin_approve`, {})
        .pipe(map((resp) => resp.details))
        .toPromise();
      this.tourLoadBillingDetails$$.next(response);
      return true;
    } catch (err) {
      this.logger.error(err);
      if (isHttpErrorResponse(err)) {
        if (isClientError(err)) {
          this.snackbar.showError(err.messageToUser);
        } else {
          this.snackbar.showError('Failed to approve load, please refresh');
        }
      } else {
        this.snackbar.showError('Failed to approve load, please refresh');
      }
    }
    return false;
  }

  public async unapproveCurrentTourLoad(): Promise<boolean> {
    const load = this.currentLoad$$.value;
    if (!load || !load.id.startsWith('LLL')) {
      return false;
    }
    return this.unapproveTourLoad(load.id);
  }

  public async unapproveTourLoad(loadID: string): Promise<boolean> {
    try {
      const response = await this.httpClient
        .post<{ details: TourBillingDetails }>(`${environment.api}/v2/load/${loadID}/tour_billing/admin_unapprove`, {})
        .pipe(map((resp) => resp.details))
        .toPromise();
      this.tourLoadBillingDetails$$.next(response);
      return true;
    } catch (err) {
      this.logger.error(err);
      if (isHttpErrorResponse(err)) {
        if (isClientError(err)) {
          this.snackbar.showError(err.messageToUser);
        } else {
          this.snackbar.showError('Failed to undo approval, please refresh');
        }
      } else {
        this.snackbar.showError('Failed to undo approval, please refresh');
      }
    }
    return false;
  }

  private async loadInProgressLoads() {
    try {
      const results = await this.httpClient
        .get<{ loads: LoHiAdminInProgressLoadListItem[] }>(`${environment.api}/v2/load/in_progress_for_company`)
        .toPromise();
      this.inProgressLoads$$.next(results.loads);
    } catch (error) {
      this.logger.error(error);
    }
  }

  private async loadBillingSummary() {
    try {
      const results = await this.httpClient
        .get<BillingSummary>(`${environment.api}/v2/load/billing_summary`)
        .toPromise();
      this.billingSummary$$.next(results);
    } catch (error) {
      this.logger.error(error);
    }
  }

  private async loadChargeTypes() {
    try {
      const results = await this.httpClient
        .get<LohiLoadAllowedChargeType[]>(`${environment.api}/v2/load/charge_types`)
        .toPromise();
      this.chargeTypes$$.next(results);
    } catch (error) {
      this.logger.error(error);
    }
  }

  private async listenForCurrentLoad() {
    this.routerState
      .listenForParamChange$(LOAD_ID)
      .pipe(
        distinctUntilChanged(),
        switchMap((loadId) => {
          if (loadId) {
            return this.loadSingleLoads$(loadId);
          }
          return of(null);
        }),
      )
      .subscribe((order: LohiLoadV2) => {
        this.currentLoad$$.next(order);
      });
  }

  private async listenForCurrentLoHiLoad() {
    this.routerState
      .listenForParamChange$(LOAD_ID)
      .pipe(
        distinctUntilChanged(),
        switchMap((loadId) => {
          if (loadId && loadId.toUpperCase().startsWith('LLL')) {
            return combineLatest([this.findTourLoadCharges$(loadId), this.getTourLoadBillingDetails$(loadId)]);
          }
          return of([null as TourBrokerCharge[], null as TourBillingDetails]);
        }),
      )
      .subscribe(([charges, details]: [TourBrokerCharge[], TourBillingDetails]) => {
        this.tourLoadCharges$$.next(charges);
        this.tourLoadBillingDetails$$.next(details);
      });
  }

  private refreshCurrentLoad() {
    return this.routerState
      .listenForParamChange$(LOAD_ID)
      .pipe(
        take(1),
        switchMap((loadId) => {
          if (loadId) {
            return this.loadSingleLoads$(loadId);
          }
          return of(null);
        }),
        tap((order: LohiLoadV2) => {
          this.currentLoad$$.next(order);
        }),
      )
      .toPromise();
  }

  private loadSingleLoads$(loadId: string): Observable<LohiLoadV2> {
    return this.httpClient.get<LohiLoadV2>(`${environment.api}/v2/load/${loadId}`);
  }

  private findTourLoadCharges$(loadId: string): Observable<TourBrokerCharge[]> {
    return this.httpClient
      .get<{ charges: TourBrokerCharge[] }>(`${environment.api}/v2/load/${loadId}/tour_billing/charge`)
      .pipe(map((resp) => resp.charges || []));
  }

  private getTourLoadBillingDetails$(loadId: string): Observable<TourBillingDetails> {
    return this.httpClient
      .get<{ details: TourBillingDetails }>(`${environment.api}/v2/load/${loadId}/tour_billing`)
      .pipe(map((resp) => resp.details));
  }

  private loadCompletedLoadsV2$(value: CompletedOrdersRequest): Observable<BillingOrdersResponse> {
    return this.httpClient.get<BillingOrdersResponse>(`${environment.api}/v2/load/completed_for_company`, {
      params: {
        ...value,
        limit: `${value.limit}`,
        offset: `${value.offset}`,
      },
    });
  }

  public resetSearch() {
    this.completedLoadsRequestObj$$.next({
      limit: 100,
      offset: 0,
      sort: 'completed_timestamp',
      order: 'DESC',
      source: 'lohi',
    });
  }

  public getLoadIssueTypes$(): Observable<SettlementStatementPaymentIssueType[]> {
    const url = `${environment.api}/v2/load/otr_list_v2/completed/issue_types`;
    return this.httpClient
      .get<{ types: SettlementStatementPaymentIssueType[] }>(url)
      .pipe(map((response) => response.types || []));
  }

  public reportLoadPaymentIssue$(loadUniqueId: string, issueTypeIds: string[]) {
    const url = `${environment.api}/v2/load/${loadUniqueId}/payment_issue`;
    return this.httpClient.post(url, { issueTypeIds });
  }

  public async refreshCompletedLoads(): Promise<void> {
    const completedLoads = await this.loadCompletedLoadsV2$(this.completedLoadsRequestObj$$.value).toPromise();
    this.completedLoadsPaginated$$.next(completedLoads);
  }
}
